import React, { useState } from 'react';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PlayerAvatar from './ui/PlayerAvatar';
import api from '../api';
import tokens from '../theme/tokens';

const hora = (d) => new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });

/**
 * Bola da pelada (tela de Confirmação): todos veem quem ficou com as bolas; o jogador informa
 * com quantas ficou (e a turma recebe uma notificação) ou que não está mais com ela.
 */
const BolaPeladaCard = ({ pelada, userLogado, onAtualizar, onMessage, sx }) => {
  const [aberto, setAberto] = useState(false);
  const [quantidade, setQuantidade] = useState(1);
  const [salvando, setSalvando] = useState(false);
  if (!pelada || !pelada._id || !userLogado) return null;

  const bolas = pelada.bolas || [];
  const minha = bolas.find((b) => String(b.user) === String(userLogado._id));
  const total = bolas.reduce((t, b) => t + (b.quantidade || 0), 0);

  const abrir = () => {
    setQuantidade(minha ? minha.quantidade : 1);
    setAberto(true);
  };

  const salvar = async (qtd) => {
    setSalvando(true);
    try {
      const { data } = await api.post('v2/pelada/bolas', { peladaId: pelada._id, quantidade: qtd });
      onAtualizar && onAtualizar(data.bolas);
      onMessage && onMessage(qtd > 0 ? `Registrado: você ficou com ${qtd} bola${qtd === 1 ? '' : 's'}. A turma foi avisada.` : 'Registrado: você não está mais com a bola.', 'success');
      setAberto(false);
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Não foi possível registrar a bola.', 'error');
    }
    setSalvando(false);
  };

  return (
    <>
      <Card sx={{ p: 2, ...sx }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: total ? tokens.color.successSoft : tokens.color.surfaceAlt, color: total ? tokens.color.success : 'text.secondary' }}>
            <SportsSoccerRoundedIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography fontWeight={700}>Bola da pelada</Typography>
            <Typography variant="body2" color="text.secondary">
              {total ? `${total} bola${total === 1 ? '' : 's'} com ${bolas.length === 1 ? 'um jogador' : `${bolas.length} jogadores`}` : 'Ninguém informou que ficou com a bola.'}
            </Typography>
          </Box>
          <Button variant={minha ? 'outlined' : 'contained'} size="small" onClick={abrir} sx={{ flexShrink: 0 }}>
            {minha ? 'Alterar' : 'Fiquei com a bola'}
          </Button>
        </Stack>
        {bolas.length > 0 && (
          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
            {bolas.map((b) => (
              <Stack key={String(b.user)} direction="row" spacing={1.25} alignItems="center" sx={{ px: 1, py: 0.75, borderRadius: 2, bgcolor: 'background.default' }}>
                <PlayerAvatar userId={b.user} name={b.user_name} size={32} />
                <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1, minWidth: 0 }} noWrap>{b.user_name}</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: tokens.color.success }}>{b.quantidade} ⚽</Typography>
                <Typography variant="caption" color="text.secondary">{hora(b.informadoEm)}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Card>

      <Dialog open={aberto} onClose={() => !salvando && setAberto(false)} fullWidth maxWidth="xs">
        <DialogTitle>Com quantas bolas você ficou?</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={3} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
            <IconButton aria-label="Menos" onClick={() => setQuantidade((q) => Math.max(1, q - 1))} disabled={quantidade <= 1} sx={{ width: 52, height: 52, border: 1, borderColor: 'divider' }}>
              <RemoveRoundedIcon />
            </IconButton>
            <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '3rem', minWidth: 60, textAlign: 'center' }}>{quantidade}</Typography>
            <IconButton aria-label="Mais" onClick={() => setQuantidade((q) => Math.min(10, q + 1))} sx={{ width: 52, height: 52, bgcolor: tokens.color.brandSoft, color: tokens.color.brand }}>
              <AddRoundedIcon />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary" align="center">A turma recebe uma notificação avisando.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          {minha ? (
            <Button color="error" onClick={() => salvar(0)} disabled={salvando}>Não estou mais</Button>
          ) : (
            <Button color="inherit" onClick={() => setAberto(false)} disabled={salvando}>Cancelar</Button>
          )}
          <Button variant="contained" startIcon={<SportsSoccerRoundedIcon />} onClick={() => salvar(quantidade)} disabled={salvando}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BolaPeladaCard;
